<template>
    <Card>
        <CompassAlert
            v-if="!isConform"
            :description="$t.__('settings.sellsymodule.invoicing.conformity.not_conform_alert')"
            type="warning"
            :showIcon="false"
            :showClose="false"
        />
        <div class="settings-conformity__help">
            <span class="typographies-paragraph-medium-regular">
                {{ $t.__('settings.sellsymodule.invoicing.conformity.information_text') }}
            </span>
            <CompassLink
                :label="$t.__('common.labels.learn_more')"
                :link="
                    $t.__(
                        'https://help.sellsy.com/fr/articles/5870717-conformite-des-documents-de-vente',
                    )
                "
                target="_blank"
            />
        </div>

        <CompassSkeleton v-if="isLoading" variant="block" />

        <template v-else>
            <div v-if="isConform" class="settings-conformity__is-conform">
                <div class="typographies-heading-h4 settings-conformity__is-conform--title">
                    {{ $t.__('settings.sellsymodule.invoicing.conformity.is_conform_title') }}
                </div>
                <div class="settings-conformity__is-conform--rules-container">
                    <div
                        v-for="(ruleText, index) in isConformRuleText"
                        :key="index"
                        class="typographies-paragraph-medium-regular settings-conformity__is-conform--rule"
                    >
                        <CompassIcon icon="success-circle-fill" type="success" />
                        {{ ruleText }}
                    </div>
                </div>
                <div
                    v-if="shouldDisplayActivatedInfo"
                    class="settings-conformity__activated-data typographies-paragraph-medium-regular"
                >
                    <div v-if="!!conformityData.activated_at">
                        {{
                            $filters.dateFormatter(
                                conformityData.activated_at,
                                'NORMAL_DATE_TIME_WITH_DAY',
                            )
                        }}
                    </div>
                    <div>
                        {{ textActivatedBy }}
                    </div>
                </div>
                <CompassTooltip
                    :content="contentTooltipDownloadDisabled"
                    :disabled="downloadCertificateEnabled"
                    placement="bottom-start"
                >
                    <CompassButton
                        class="settings-conformity__is-conform--download-certificate"
                        :disabled="!downloadCertificateEnabled"
                        :label="
                            $t.__('settings.sellsymodule.invoicing.conformity.download_certificate')
                        "
                        @click="downloadCertificate"
                    />
                </CompassTooltip>
            </div>
            <div v-else class="settings-conformity__is-not-conform">
                <CompassObject
                    :label="$t.__('settings.sellsymodule.invoicing.conformity.not_conform_title')"
                    object="document"
                    size="small"
                />
                <CompassTooltip
                    :content="$t.__('common.labels.no_permissions_to_perform_this_action')"
                    :disabled="iscreatable"
                    placement="bottom-end"
                >
                    <CompassButton
                        :disabled="!iscreatable"
                        :label="
                            $t.__(
                                'settings.sellsymodule.invoicing.conformity.button_activate_conformity',
                            )
                        "
                        @click="handleActivateConformity"
                    />
                </CompassTooltip>
            </div>
        </template>
    </Card>
</template>
<script>
import Card from 'vcomponents/common/card/Card.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapGetters: corpGetters } = createNamespacedHelpers('corp');
const { mapState: mapStateSystem } = createNamespacedHelpers('system');
import { getConformityData } from 'api/settings/invoicing/conformity/ConformityService';
import { getFullNameUser } from 'Helpers/avatarHelper';
export default {
    components: {
        Card,
    },
    props: {
        //used to display activate conformity btn in next ticket
        iscreatable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            conformityData: null,
            isLoading: false,
            loading: false,
            isConfirm: false,
            conformityResponse: null,
            isConformRuleText: [
                this.$t.__(
                    'settings.sellsymodule.invoicing.conformity.is_conform_credit_note_creation',
                ),
                this.$t.__('settings.sellsymodule.invoicing.conformity.is_conform_invoice_number'),
                this.$t.__('settings.sellsymodule.invoicing.conformity.is_conform_invoice_date'),
            ],
        };
    },
    computed: {
        ...corpGetters({
            isTrial: 'getIsTrial',
        }),
        ...mapStateSystem(['errorsHandler']),
        shouldDisplayActivatedInfo() {
            return !!this.conformityData?.activated_at && !!this.conformityData?.activated_by;
        },
        downloadCertificateEnabled() {
            return !this.isTrial && !!this.conformityData?.pdf;
        },
        textActivatedBy() {
            if (!this.conformityData.activated_by) {
                return '';
            }
            return this.$t.__('settings.sellsymodule.invoicing.conformity.activated_by', {
                fullName: getFullNameUser(this.conformityData.activated_by),
                email: this.conformityData.activated_by.email,
            });
        },
        contentTooltipDownloadDisabled() {
            if (this.isTrial) {
                return this.$t.__('settings.sellsymodule.invoicing.conformity.download_is_trial');
            }
            if (!this.conformityData?.pdf) {
                return this.$t.__(
                    'settings.sellsymodule.invoicing.conformity.certificate_is_generating',
                );
            }
            return '';
        },
        isConform() {
            return !!this.conformityData && this.conformityData.active;
        },
    },
    async created() {
        this.isLoading = true;
        try {
            this.conformityData = (await getConformityData()).data;
            EventBus.on('conformity:generate', (event) => {
                this.conformityData.pdf = event.pdf.url;
            });
        } catch (error) {
            this.$pushToast({
                type: 'error',
                icon: true,
                text: this.$t.__('settings.sellsymodule.invoicing.conformity.error_get_data'),
            });
            console.error(error);
            this.errorsHandler.logException(error);
        } finally {
            this.isLoading = false;
        }
    },
    mounted() {
        if (Object.hasOwn(this.$route.query, 'success')) {
            this.$pushToast({
                type: 'success',
                icon: true,
                text: this.$t.__(
                    'settings.sellsymodule.invoicing.enable_conformity.success_conformity_activation',
                ),
            });
            const url = new URL(location.href);
            if (url.search.length > 0) {
                url.search = '';
                history.replaceState(null, document.title, url.toString());
            }
        }
    },
    destroyed() {
        EventBus.off('conformity:generate');
    },
    methods: {
        downloadCertificate() {
            window.open(this.conformityData?.pdf, '_blank');
        },
        handleActivateConformity() {
            window.location.href = '/documents/preferences/conformity/enable';
        },
    },
};
</script>

<style scoped lang="scss">
.settings-conformity {
    background-color: var(--colors-common-background-default);
    border: 1px solid var(--colors-common-border-default);
    border-radius: var(--spacing-0);
    padding: var(--spacing-4);
    width: 100%;

    .typographies-heading-h2 {
        margin-bottom: var(--spacing-3);
    }

    &__help {
        margin-bottom: var(--spacing-3);
    }

    &__is-conform {
        border: 1px solid var(--colors-common-border-default);
        border-radius: var(--spacing-0);
        padding: var(--spacing-3);

        &--title {
            margin-bottom: var(--spacing-1);
        }

        &--rule {
            display: flex;
            gap: var(--spacing-0);
        }

        &--rules-container {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
            margin-bottom: var(--spacing-4);

            &:has(+ .settings-conformity__activated-data) {
                border-bottom: 1px solid var(--colors-common-border-default);
                padding-bottom: var(--spacing-3);
            }
        }

        .settings-conformity__activated-data {
            color: var(--colors-common-text-standard-caption);
            margin-bottom: var(--spacing-3);
        }
    }

    &__is-not-conform {
        background-color: var(--colors-common-background-subtle);
        border-radius: var(--spacing-0);
        display: flex;
        gap: var(--spacing-1);
        justify-content: space-between;
        padding: var(--spacing-3);
    }
}
</style>
